import type {AppPayload} from '@github-ui/issues-shared/app-payload'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload -- Cannot use UI Service because we're within Relay environment */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useQuery} from '@github-ui/react-query'
import {useParams} from '@github-ui/react-router'
import type {SidebarPinnedView} from '@github-ui/repos-issues-sidebar-ui/PinnedViewsNav'
import {ReposIssuesSidebar as SharedRepoIssuesSidebar} from '@github-ui/repos-issues-sidebar-ui/ReposIssuesSidebar'
import {useMemo} from 'react'

import {usePreloadSidebarPages} from '../../hooks/use-preload-sidebar-pages'
import {savedViewsQueryOptions} from '../../queries/saved-views'
import {RepoSavedViewsNav} from './RepoSavedViewsNav'

export function RepoIssuesSidebar({collapsible}: {collapsible?: boolean} = {}) {
  usePreloadSidebarPages()
  const {owner, repo} = useParams<{owner: string; repo: string}>()
  const {scoped_repository} = useAppPayload<AppPayload>()
  const hasProjectsEnabled = scoped_repository?.has_projects_enabled ?? false
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user. */
  const pinnedViewsEnabled = useFeatureFlag('issue_pinned_views')
  const pinnedViews = usePinnedViews(owner ?? '', repo ?? '', pinnedViewsEnabled)
  return (
    <SharedRepoIssuesSidebar
      owner={owner ?? ''}
      repo={repo ?? ''}
      collapsible={collapsible}
      hasProjectsEnabled={hasProjectsEnabled}
      pinnedViews={pinnedViews}
      renderSavedViewsNav={
        pinnedViewsEnabled ? undefined : ctx => <RepoSavedViewsNav owner={owner ?? ''} repo={repo ?? ''} {...ctx} />
      }
    />
  )
}

function usePinnedViews(owner: string, repo: string, enabled: boolean): SidebarPinnedView[] {
  const {data} = useQuery({...savedViewsQueryOptions({owner, repo}), enabled})
  return useMemo(
    () =>
      enabled
        ? (data?.savedViews ?? [])
            .filter(view => view.pinned)
            .map(view => ({id: view.id, name: view.name, icon: view.icon, color: view.color}))
        : [],
    [data, enabled],
  )
}
